import clsx from "clsx";
import { memo, useCallback, useEffect, useMemo, useState } from "react";

import { Typography } from "@atoms/typography";
import { FastLinkButton } from "@components/fastFields/fastLinkButton";
import { getThemeColor } from "@theme";

import classes from "./styles.module.scss";

import type { IResendCodeProps } from "@templates/authManagement/resendCode/types";

import "ldrs/dotPulse";

export const ResendCode = memo(function ResendCode({
  onResendCode,
  onTimerFinish,
  isLoading,
}: IResendCodeProps) {
  const [timeLeft, setTimeLeft] = useState(60);
  const [isTimerStart, setIsTimerStart] = useState(true);

  useEffect(() => {
    if (!isTimerStart) {
      onTimerFinish();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isTimerStart]);

  useEffect(() => {
    if (!isTimerStart) {
      return;
    }

    const intervalId = setInterval(() => {
      setTimeLeft((prevState) => prevState - 1);
    }, 1000);

    return () => {
      clearInterval(intervalId);
    };
  }, [isTimerStart]);

  useEffect(() => {
    if (timeLeft === 0) setIsTimerStart(false);
  }, [timeLeft]);

  const formatTime = useCallback((time: number) => {
    const minutes = Math.floor(time / 60);
    const seconds = time % 60;

    return `${minutes}:${seconds < 10 ? "0" : ""}${seconds}`;
  }, []);

  const timeText = useMemo(() => formatTime(timeLeft), [formatTime, timeLeft]);

  if (isLoading) {
    return (
      <div className={clsx(clsx(classes.resend))}>
        <l-dot-pulse
          size={"43"}
          speed={"1.3"}
          color={getThemeColor("text-strong-900").colorValue}
        />
      </div>
    );
  }

  return (
    <div
      className={clsx(clsx(classes.resend))}
      onClick={async () => {
        if (!isTimerStart) {
          await onResendCode();
          setIsTimerStart(true);
          setTimeLeft(60);
        }
      }}
    >
      <Typography
        fontType={"label-small"}
        className={clsx(!isTimerStart && "cursor-pointer")}
        disableSelect
      >
        {isTimerStart ? timeText : <FastLinkButton type={"resendCode"} />}
      </Typography>
    </div>
  );
});
